import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  NOTIFICATION_DEFAULT_DELAY,
  NOTIFICATION_DEFAULT_ERROR_MESSAGE,
  useNotificationContext,
} from 'application/providers/NotificationProvider';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useAccountCompanyId } from 'application/hooks';
import { useAppSelector } from 'application/providers/StoreProvider';
import { selectCompanyIds, selectUserData } from 'application/store';
import { DEFAULT_POINT_STATUSES } from 'application/pages/PointsPage/renderer/PointsToolbar/consts';

import {
  AdcampaignPlaybackReportTypeForInput,
  CompanyAdCampaignsEarlyReportMutation,
  useMediaCompanyAdCampaignsEarlyReportMutation,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import useAllPointList from 'domain/point/useAllPointList';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import {
  EMAIL_REGEXP,
  FIRST_REFETCH_INTERVAL,
  MAX_EXTERNAL_RECIPIENT_EMAILS_COUNT,
  MAX_REPORT_RECIPIENT_EMAILS_COUNT,
} from './consts';
import { TEXTS } from './texts';
import { AUDIO_REPORT_SCOPE, AudioReportScopeType } from './types';
import { REPORT_MODAL_TITLE_BY_REPORT_TYPE } from './reportTypeTitles';
import {
  areStringArraysEqual,
  getNumericPointIds,
  getReportAddressTags,
  getReportRequestExistsNotification,
  getReportRequestErrorMessage,
  getReportRequestErrorsMessage,
  getReportRequestSuccessNotification,
  mapPointToReportAddress,
  validateDateRange,
} from './utils';

type UseRequestAdCampaignsReportViewModelProps = Pick<
  ModalProps[ModalTypes.REPORT_CREATE_AD_CAMPAIGNS],
  'adcampaignReportType' | 'close' | 'refetchReports'
>;

export const useRequestAdCampaignsReportViewModel = ({
  adcampaignReportType,
  close,
  refetchReports,
}: UseRequestAdCampaignsReportViewModelProps) => {
  const notification = useNotificationContext();
  const { isMobile } = useMedia();
  const userData = useAppSelector(selectUserData);
  const companyId = useAccountCompanyId();
  const storedCompanyIds = useAppSelector(selectCompanyIds);

  const [mutation, { loading }] = useMediaCompanyAdCampaignsEarlyReportMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  const companyIds = useMemo(
    () => (storedCompanyIds.length > 0 ? storedCompanyIds : [companyId]),
    [companyId, storedCompanyIds]
  );
  const companyIdsKey = companyIds.join(',');
  const hasCompanyIds = companyIds.length > 0;

  const allPointList = useAllPointList({
    companyIds,
    playingStatuses: DEFAULT_POINT_STATUSES,
  });
  const requestPointsRef = useRef(allPointList.request);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isExternalRecipientsInputVisible, setIsExternalRecipientsInputVisible] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
  const [externalRecipientEmails, setExternalRecipientEmails] = useState<string[]>([]);
  const [reportScope, setReportScope] = useState<AudioReportScopeType>(AUDIO_REPORT_SCOPE.COMMON);
  const [selectedAddressIds, setSelectedAddressIds] = useState<string[]>([]);
  const [isAddressSelectionTouched, setIsAddressSelectionTouched] = useState(false);

  const isAudioAdsReport = adcampaignReportType === AdcampaignPlaybackReportTypeForInput.AudioAds;
  const isAddressReportScope = isAudioAdsReport && reportScope === AUDIO_REPORT_SCOPE.ADDRESSES;
  const isPromoReportScope = isAudioAdsReport && reportScope === AUDIO_REPORT_SCOPE.PROMOS;

  useEffect(() => {
    requestPointsRef.current = allPointList.request;
  }, [allPointList.request]);

  useEffect(() => {
    if (!isAddressReportScope || !hasCompanyIds) {
      return;
    }

    requestPointsRef.current().catch(() => {
      notification.showErrorNotification({
        children: NOTIFICATION_DEFAULT_ERROR_MESSAGE,
      });
    });
  }, [companyIdsKey, hasCompanyIds, isAddressReportScope, notification]);

  const reportAddresses = useMemo(
    () => (allPointList.points || []).map(mapPointToReportAddress),
    [allPointList.points]
  );
  const reportAddressTags = useMemo(() => getReportAddressTags(reportAddresses), [reportAddresses]);
  const reportAddressIds = useMemo(() => reportAddresses.map((address) => address.id), [reportAddresses]);
  const isReportAddressesLoading = isAddressReportScope && (allPointList.isReloading || allPointList.loading);
  const selectedReportPointIds = useMemo(() => getNumericPointIds(selectedAddressIds), [selectedAddressIds]);

  useEffect(() => {
    if (!isAddressReportScope) {
      return;
    }

    const addressIdSet = new Set(reportAddressIds);

    setSelectedAddressIds((currentAddressIds) => {
      const nextAddressIds = isAddressSelectionTouched
        ? currentAddressIds.filter((addressId) => addressIdSet.has(addressId))
        : reportAddressIds;

      return areStringArraysEqual(currentAddressIds, nextAddressIds) ? currentAddressIds : nextAddressIds;
    });
  }, [isAddressReportScope, isAddressSelectionTouched, reportAddressIds]);

  const recipients = useMemo(() => {
    const users = userData?.mainCompany?.users || [];

    if (users.length > 0) {
      return users;
    }

    if (userData?.id && userData.email) {
      return [{ id: userData.id, email: userData.email }];
    }

    return [];
  }, [userData]);

  const recipientIds = useMemo(() => recipients.map((recipient) => recipient.id), [recipients]);

  useEffect(() => {
    setSelectedRecipientIds(recipientIds);
  }, [recipientIds]);

  const selectedRecipientEmails = useMemo(() => {
    const selectedIds = new Set(selectedRecipientIds);

    return recipients.filter((recipient) => selectedIds.has(recipient.id)).map((recipient) => recipient.email);
  }, [recipients, selectedRecipientIds]);

  const recipientEmailSet = useMemo(
    () => new Set(recipients.map((recipient) => recipient.email.trim().toLowerCase()).filter(Boolean)),
    [recipients]
  );

  const invalidExternalRecipientEmails = useMemo(
    () => externalRecipientEmails.filter((email) => !EMAIL_REGEXP.test(email)),
    [externalRecipientEmails]
  );

  const reportRecipientEmails = useMemo(() => {
    const emails = isAudioAdsReport ? [...selectedRecipientEmails, ...externalRecipientEmails] : [];
    const usedEmails = new Set<string>();

    return emails
      .map((email) => email.trim())
      .filter((email) => {
        if (!email || !EMAIL_REGEXP.test(email)) {
          return false;
        }

        const normalizedEmail = email.toLowerCase();

        if (usedEmails.has(normalizedEmail)) {
          return false;
        }

        usedEmails.add(normalizedEmail);

        return true;
      });
  }, [externalRecipientEmails, isAudioAdsReport, selectedRecipientEmails]);

  const externalReportRecipientEmailsCount = useMemo(
    () => reportRecipientEmails.filter((email) => !recipientEmailSet.has(email.toLowerCase())).length,
    [recipientEmailSet, reportRecipientEmails]
  );

  const hasInvalidExternalRecipients = invalidExternalRecipientEmails.length > 0;
  const hasTooManyExternalRecipients =
    isAudioAdsReport && externalReportRecipientEmailsCount > MAX_EXTERNAL_RECIPIENT_EMAILS_COUNT;
  const hasTooManyReportRecipients =
    isAudioAdsReport && reportRecipientEmails.length > MAX_REPORT_RECIPIENT_EMAILS_COUNT;
  const hasReportRecipients = !isAudioAdsReport || reportRecipientEmails.length > 0;
  const hasSelectedReportAddresses = !isAddressReportScope || selectedAddressIds.length > 0;
  const isRequestDisabled =
    isPromoReportScope ||
    startDate === null ||
    endDate === null ||
    loading ||
    isReportAddressesLoading ||
    !hasReportRecipients ||
    !hasSelectedReportAddresses ||
    hasInvalidExternalRecipients ||
    hasTooManyExternalRecipients ||
    hasTooManyReportRecipients;

  const onDateRangeChange = useCallback((startDate: Date | null, endDate: Date | null) => {
    setStartDate(startDate);
    setEndDate(endDate);
  }, []);

  const onShowExternalRecipientsInput = useCallback(() => {
    setIsExternalRecipientsInputVisible(true);
  }, []);

  const onSelectedRecipientIdsChange = useCallback((recipientIds: string[]) => {
    setSelectedRecipientIds(recipientIds);
  }, []);

  const onExternalRecipientEmailsChange = useCallback((emails: string[]) => {
    setExternalRecipientEmails(emails);
  }, []);

  const onReportScopeChange = useCallback((scope: AudioReportScopeType) => {
    setReportScope(scope);

    if (scope === AUDIO_REPORT_SCOPE.ADDRESSES) {
      setIsAddressSelectionTouched(false);
    }
  }, []);

  const onSelectedAddressIdsChange = useCallback((addressIds: string[]) => {
    setIsAddressSelectionTouched(true);
    setSelectedAddressIds(addressIds);
  }, []);

  const onRequestSubmit = useCallback(async () => {
    try {
      if (isPromoReportScope || startDate === null || endDate === null) return;

      const validationError = validateDateRange(startDate, endDate);

      if (validationError) {
        notification.showErrorNotification({
          children: validationError,
          delay: NOTIFICATION_DEFAULT_DELAY,
        });

        return;
      }

      if (isAudioAdsReport && hasInvalidExternalRecipients) {
        notification.showErrorNotification({
          children: TEXTS.EXTERNAL_RECIPIENTS_INVALID_ERROR,
          delay: NOTIFICATION_DEFAULT_DELAY,
        });

        return;
      }

      if (isAudioAdsReport && reportRecipientEmails.length === 0) {
        notification.showErrorNotification({
          children: TEXTS.REPORT_RECIPIENTS_EMPTY_ERROR,
          delay: NOTIFICATION_DEFAULT_DELAY,
        });

        return;
      }

      if (hasTooManyExternalRecipients) {
        notification.showErrorNotification({
          children: TEXTS.EXTERNAL_RECIPIENTS_MAX_COUNT_ERROR,
          delay: NOTIFICATION_DEFAULT_DELAY,
        });

        return;
      }

      if (hasTooManyReportRecipients) {
        notification.showErrorNotification({
          children: TEXTS.REPORT_RECIPIENTS_MAX_COUNT_ERROR,
          delay: NOTIFICATION_DEFAULT_DELAY,
        });

        return;
      }

      if (isAddressReportScope && selectedAddressIds.length === 0) {
        notification.showErrorNotification({
          children: TEXTS.REPORT_ADDRESSES_EMPTY_ERROR,
          delay: NOTIFICATION_DEFAULT_DELAY,
        });

        return;
      }

      const result = await mutation({
        variables: {
          input: {
            companyId,
            adcampaignReportType,
            emailsTo: isAudioAdsReport ? reportRecipientEmails : [],
            ...(isAddressReportScope ? { pointIds: selectedReportPointIds } : {}),
            dateRange: {
              dateFrom: DateUtils.getStartOfDate(startDate).toISOString(),
              dateTo: DateUtils.getEndOfDate(endDate).toISOString(),
            },
          },
        },
      });

      const reportResult = result.data?.result as CompanyAdCampaignsEarlyReportMutation | null | undefined;

      if (!reportResult || !reportResult.ok) {
        notification.showErrorNotification({
          children: getReportRequestErrorsMessage(reportResult?.errors) || NOTIFICATION_DEFAULT_ERROR_MESSAGE,
        });

        return;
      }

      if (!reportResult.isCreated) {
        notification.showInfoNotification({
          children: getReportRequestExistsNotification(adcampaignReportType),
          delay: NOTIFICATION_DEFAULT_DELAY * 3,
        });

        return;
      }

      close();

      notification.showInfoNotification({
        children: getReportRequestSuccessNotification(adcampaignReportType),
        delay: NOTIFICATION_DEFAULT_DELAY * 3,
      });

      setTimeout(() => {
        refetchReports();
      }, FIRST_REFETCH_INTERVAL);
    } catch (e) {
      const error = e as Error;
      const notificationMessage = getReportRequestErrorMessage(error.message);

      notification.showErrorNotification({
        children: notificationMessage || NOTIFICATION_DEFAULT_ERROR_MESSAGE,
      });
    }
  }, [
    adcampaignReportType,
    close,
    companyId,
    endDate,
    hasInvalidExternalRecipients,
    hasTooManyExternalRecipients,
    hasTooManyReportRecipients,
    isAddressReportScope,
    isAudioAdsReport,
    isPromoReportScope,
    mutation,
    notification,
    refetchReports,
    reportRecipientEmails,
    selectedAddressIds.length,
    selectedReportPointIds,
    startDate,
  ]);

  const modalTitle = REPORT_MODAL_TITLE_BY_REPORT_TYPE[adcampaignReportType] || TEXTS.REQUEST_REPORT_MODAL_TITLE;
  const requestButtonText = isAudioAdsReport ? TEXTS.REQUEST_AUDIO_REPORT : TEXTS.REQUEST;
  const closeButtonText = isAudioAdsReport ? TEXTS.CANCEL : TEXTS.CLOSE;

  return {
    closeButtonText,
    endDate,
    externalRecipientEmails,
    isAudioAdsReport,
    isExternalRecipientsInputVisible,
    isMobile,
    isRequestDisabled,
    isReportAddressesLoading,
    loading,
    modalTitle,
    onDateRangeChange,
    onExternalRecipientEmailsChange,
    onReportScopeChange,
    onRequestSubmit,
    onSelectedAddressIdsChange,
    onSelectedRecipientIdsChange,
    onShowExternalRecipientsInput,
    recipients,
    reportAddresses,
    reportAddressTags,
    reportScope,
    requestButtonText,
    selectedAddressIds,
    selectedRecipientIds,
    startDate,
  };
};
